import type { AddressData } from '@shopmuchmore/design-system'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useAuthStore } from '../../auth/auth.store'
import {
  useCreateShippingAddress,
  useDeleteShippingAddress,
  useSetShippingAddressDefault,
  useShippingAddresses,
} from '../../shipping/shipping.services'
import { useLocationStore } from '../store/location.store'
import {
  DEFAULT_COORDINATES,
  geocodeAddress,
  reverseGeocode,
  reverseGeocodeAddressDetails,
} from '../utils/geocoding.utils'

const normalize = (str?: string | null) =>
  str
    ? str
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .trim()
    : ''

const sanitizeCoordinate = (val?: number | string | null): number | null => {
  if (val === undefined || val === null || val === '') return null
  const num = Number(val)
  return Number.isNaN(num) ? null : Number(num.toFixed(6))
}

let isGlobalAddressSyncing = false
const syncedAddressesSet = new Set<string>()

export function useLocationPicker() {
  const {
    latitude,
    longitude,
    address,
    method,
    lastGpsLat,
    lastGpsLng,
    lastGpsAddress,
    setLocation,
    setLastGpsLocation,
    clearLocation,
  } = useLocationStore()

  const { customer } = useAuthStore()
  const isAuthenticated = !!customer

  const [isLoadingGPS, setIsLoadingGPS] = useState(false)
  const [userCountryCode, setUserCountryCode] = useState<string | null>(null)
  const [isLocationModalOpen, setLocationModalOpen] = useState(false)

  const { data: addressesData, isLoading: isLoadingAddress } = useShippingAddresses()
  const createAddressMutation = useCreateShippingAddress()
  const setDefaultMutation = useSetShippingAddressDefault()
  const deleteMutation = useDeleteShippingAddress()

  const savedAddresses = Array.isArray(addressesData) ? addressesData : (addressesData as any)?.results || []

  // Silently detect country code for regional settings
  useEffect(() => {
    fetch('https://api.country.is')
      .then((res) => res.json())
      .then((data) => {
        if (data?.country) {
          setUserCountryCode(data.country)
        }
      })
      .catch(() => {
        // Silently fail
      })
  }, [])

  const findMatchingSavedAddress = useCallback(
    (targetAddress?: string | null, targetLat?: number | null, targetLng?: number | null) => {
      if (!targetAddress && (targetLat == null || targetLng == null)) return undefined

      const targetNorm = normalize(targetAddress)
      return savedAddresses.find((addr: any) => {
        const streetNorm = normalize(addr.street_address)
        if (
          targetNorm &&
          streetNorm &&
          (streetNorm === targetNorm || streetNorm.includes(targetNorm) || targetNorm.includes(streetNorm))
        ) {
          return true
        }
        if (targetLat != null && targetLng != null && addr.latitude != null && addr.longitude != null) {
          const latDiff = Math.abs(Number(addr.latitude) - targetLat)
          const lngDiff = Math.abs(Number(addr.longitude) - targetLng)
          if (latDiff < 0.0005 && lngDiff < 0.0005) {
            return true
          }
        }
        return false
      })
    },
    [savedAddresses],
  )

  // Auto-sync guest address to user's saved shipping addresses upon login (only once)
  useEffect(() => {
    if (!isAuthenticated || !customer || !address || method === 'saved' || isLoadingAddress || isGlobalAddressSyncing) {
      return
    }

    const syncKey = `${(customer as any)?.id || customer.email || ''}_${address}`
    if (syncedAddressesSet.has(syncKey)) {
      return
    }

    const cleanLat = sanitizeCoordinate(latitude)
    const cleanLng = sanitizeCoordinate(longitude)
    const matchingSaved = findMatchingSavedAddress(address, cleanLat, cleanLng)

    if (matchingSaved) {
      syncedAddressesSet.add(syncKey)
      const slug = matchingSaved.shipping_slug || (matchingSaved.id ? String(matchingSaved.id) : undefined)
      if (slug && !matchingSaved.is_default && !setDefaultMutation.isPending) {
        setDefaultMutation.mutateAsync(slug).catch((err) => {
          console.warn('Failed to set matching address as default:', err)
        })
      }
      const finalLat = cleanLat ?? DEFAULT_COORDINATES.lat
      const finalLng = cleanLng ?? DEFAULT_COORDINATES.lng
      setLocation(finalLat, finalLng, address, 'saved')
    } else if (!createAddressMutation.isPending) {
      isGlobalAddressSyncing = true
      syncedAddressesSet.add(syncKey)

      const syncGuestAddress = async () => {
        const finalLat = cleanLat ?? DEFAULT_COORDINATES.lat
        const finalLng = cleanLng ?? DEFAULT_COORDINATES.lng

        try {
          const details = cleanLat && cleanLng ? await reverseGeocodeAddressDetails(cleanLat, cleanLng) : null
          const firstName = customer.first_name || 'My'
          const lastName = customer.last_name || 'Address'
          const email = customer.email || 'customer@shopmuchmore.com'
          const phone = customer.phone || (customer as any)?.phone_number || '+447000000000'

          await createAddressMutation.mutateAsync({
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone_number: phone,
            street_address: details?.street_address || address,
            city: details?.city || 'London',
            postal_code: details?.postal_code || '',
            state_or_region: details?.state_or_region || 'London',
            country: details?.country || 'GB',
            latitude: cleanLat,
            longitude: cleanLng,
            is_default: true,
          })

          setLocation(finalLat, finalLng, address, 'saved')
        } catch (err) {
          console.warn('Auto-syncing guest address to profile failed:', err)
          // Always transition state to 'saved' so we don't spam the server in an infinite retry loop
          setLocation(finalLat, finalLng, address, 'saved')
        } finally {
          isGlobalAddressSyncing = false
        }
      }
      syncGuestAddress()
    }
  }, [
    isAuthenticated,
    customer,
    address,
    method,
    isLoadingAddress,
    latitude,
    longitude,
    createAddressMutation,
    setDefaultMutation,
    setLocation,
    findMatchingSavedAddress,
  ])

  /**
   * Acquire browser geolocation, reverse-geocode readable address, and update active location store.
   */
  const useCurrentLocation = useCallback(
    async (onSuccess?: () => void) => {
      if (!('geolocation' in navigator)) {
        toast.error('Geolocation is not supported by your browser')
        return
      }

      setIsLoadingGPS(true)
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude: lat, longitude: lng, accuracy } = position.coords
          try {
            const details = await reverseGeocodeAddressDetails(lat, lng)
            const locationName = details?.formatted_address || (await reverseGeocode(lat, lng)) || 'Current Location'

            setLastGpsLocation(lat, lng, locationName)

            if (isAuthenticated) {
              const cleanLat = sanitizeCoordinate(lat)
              const cleanLng = sanitizeCoordinate(lng)
              const matchingSaved = findMatchingSavedAddress(locationName, cleanLat, cleanLng)
              if (matchingSaved) {
                const slug = matchingSaved.shipping_slug || (matchingSaved.id ? String(matchingSaved.id) : undefined)
                if (slug && !matchingSaved.is_default) {
                  try {
                    await setDefaultMutation.mutateAsync(slug)
                  } catch (e) {
                    console.warn('Failed to set matching address as default:', e)
                  }
                }
                setLocation(
                  cleanLat ?? DEFAULT_COORDINATES.lat,
                  cleanLng ?? DEFAULT_COORDINATES.lng,
                  locationName,
                  'saved',
                )
              } else {
                try {
                  const firstName = customer?.first_name || 'My'
                  const lastName = customer?.last_name || 'Address'
                  const email = customer?.email || 'customer@shopmuchmore.com'
                  const phone = customer?.phone || (customer as any)?.phone_number || '+447000000000'

                  await createAddressMutation.mutateAsync({
                    first_name: firstName,
                    last_name: lastName,
                    email: email,
                    phone_number: phone,
                    street_address: details?.street_address || locationName,
                    city: details?.city || 'London',
                    postal_code: details?.postal_code || '',
                    state_or_region: details?.state_or_region || 'London',
                    country: details?.country || 'GB',
                    latitude: cleanLat,
                    longitude: cleanLng,
                    is_default: true,
                  })
                  setLocation(
                    cleanLat ?? DEFAULT_COORDINATES.lat,
                    cleanLng ?? DEFAULT_COORDINATES.lng,
                    locationName,
                    'saved',
                  )
                } catch (e) {
                  console.warn('Auto-saving GPS default shipping address failed:', e)
                  setLocation(
                    cleanLat ?? DEFAULT_COORDINATES.lat,
                    cleanLng ?? DEFAULT_COORDINATES.lng,
                    locationName,
                    'gps',
                  )
                }
              }
            } else {
              const cleanLat = sanitizeCoordinate(lat) ?? DEFAULT_COORDINATES.lat
              const cleanLng = sanitizeCoordinate(lng) ?? DEFAULT_COORDINATES.lng
              setLocation(cleanLat, cleanLng, locationName, 'gps')
            }

            if (accuracy && accuracy > 15000) {
              toast.info('Location is approximate based on your network. You can also search your exact address.')
            } else {
              toast.success('Location updated successfully')
            }
            onSuccess?.()
          } catch {
            toast.error('Failed to determine location details')
          } finally {
            setIsLoadingGPS(false)
          }
        },
        (error) => {
          console.error('Location error:', error)
          toast.error('Unable to access your location. Please check your permissions.')
          setIsLoadingGPS(false)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      )
    },
    [
      setLastGpsLocation,
      setLocation,
      isAuthenticated,
      customer,
      createAddressMutation,
      setDefaultMutation,
      findMatchingSavedAddress,
    ],
  )

  /**
   * Handle Google Places autocomplete selection, ensuring coordinates are resolved and persisted.
   */
  const selectAddress = useCallback(
    async (addressData: AddressData, onSuccess?: () => void) => {
      let lat = addressData.latitude
      let lng = addressData.longitude
      const formatted =
        addressData.formatted_address ||
        [addressData.address_line_1, addressData.city, addressData.country].filter(Boolean).join(', ')

      if ((!lat || !lng) && (formatted || addressData.place_id)) {
        const coords = await geocodeAddress({
          address: formatted,
          placeId: addressData.place_id,
        })
        if (coords) {
          lat = coords.lat
          lng = coords.lng
        }
      }

      const finalAddress = formatted || addressData.address_line_1 || 'Selected Address'
      const cleanLat = sanitizeCoordinate(lat) ?? DEFAULT_COORDINATES.lat
      const cleanLng = sanitizeCoordinate(lng) ?? DEFAULT_COORDINATES.lng

      // If customer is authenticated, automatically deduplicate and save with method 'saved'
      if (isAuthenticated) {
        const matchingSaved = findMatchingSavedAddress(finalAddress, cleanLat, cleanLng)
        if (matchingSaved) {
          const slug = matchingSaved.shipping_slug || (matchingSaved.id ? String(matchingSaved.id) : undefined)
          if (slug && !matchingSaved.is_default) {
            try {
              await setDefaultMutation.mutateAsync(slug)
            } catch (e) {
              console.warn('Failed to set matching address as default:', e)
            }
          }
          setLocation(cleanLat, cleanLng, finalAddress, 'saved')
        } else {
          try {
            const street =
              addressData.street_address || addressData.address_line_1 || addressData.formatted_address || finalAddress
            const city = addressData.city || 'London'
            const country = addressData.country || 'GB'
            const postal_code = addressData.postal_code || ''
            const state_or_region = addressData.state_or_region || city || 'London'
            const firstName = customer?.first_name || 'My'
            const lastName = customer?.last_name || 'Address'
            const email = customer?.email || 'customer@shopmuchmore.com'
            const phone = customer?.phone || (customer as any)?.phone_number || '+447000000000'

            await createAddressMutation.mutateAsync({
              first_name: firstName,
              last_name: lastName,
              email: email,
              phone_number: phone,
              street_address: street,
              city: city,
              postal_code: postal_code,
              state_or_region: state_or_region,
              country: country,
              latitude: cleanLat,
              longitude: cleanLng,
              is_default: true,
            })
            setLocation(cleanLat, cleanLng, finalAddress, 'saved')
          } catch (e) {
            console.warn('Auto-saving default shipping address failed:', e)
            setLocation(cleanLat, cleanLng, finalAddress, 'manual')
          }
        }
      } else {
        setLocation(cleanLat, cleanLng, finalAddress, 'manual')
      }

      toast.success('Location updated successfully')
      onSuccess?.()
    },
    [setLocation, isAuthenticated, customer, createAddressMutation, setDefaultMutation, findMatchingSavedAddress],
  )

  /**
   * Handle selecting a saved user shipping address.
   */
  const selectSavedAddress = useCallback(
    async (savedAddr: any, onSuccess?: () => void) => {
      let lat = savedAddr.latitude || savedAddr.lat
      let lng = savedAddr.longitude || savedAddr.lng
      const formatted = [savedAddr.street_address, savedAddr.city, savedAddr.country].filter(Boolean).join(', ')

      if ((!lat || !lng) && formatted) {
        const coords = await geocodeAddress({ address: formatted })
        if (coords) {
          lat = coords.lat
          lng = coords.lng
        }
      }

      const finalAddress = formatted || 'Saved Address'
      const finalLat = lat !== undefined && lat !== null ? Number(lat) : DEFAULT_COORDINATES.lat
      const finalLng = lng !== undefined && lng !== null ? Number(lng) : DEFAULT_COORDINATES.lng

      setLocation(finalLat, finalLng, finalAddress, 'saved')

      const slug = savedAddr.shipping_slug || (savedAddr.id ? String(savedAddr.id) : undefined)
      if (slug && !savedAddr.is_default) {
        try {
          await setDefaultMutation.mutateAsync(slug)
        } catch (e) {
          console.warn('Failed to set selected address as default:', e)
        }
      }

      toast.success('Location updated to your saved address')
      onSuccess?.()
    },
    [setLocation, setDefaultMutation],
  )

  /**
   * Delete a saved shipping address
   */
  const deleteSavedAddress = useCallback(
    async (id: string | number) => {
      try {
        await deleteMutation.mutateAsync(String(id))
        toast.success('Address deleted successfully')
      } catch {
        toast.error('Failed to delete address')
      }
    },
    [deleteMutation],
  )

  /**
   * Set a saved address as the default
   */
  const setDefaultAddress = useCallback(
    async (id: string | number) => {
      try {
        await setDefaultMutation.mutateAsync(String(id))
        toast.success('Default address updated')
      } catch {
        toast.error('Failed to set default address')
      }
    },
    [setDefaultMutation],
  )

  return {
    // Location Store State
    latitude,
    longitude,
    address,
    method,
    lastGpsLat,
    lastGpsLng,
    lastGpsAddress,
    hasLocation: !!address && latitude !== null && longitude !== null,
    setLocation,
    clearLocation,

    // Geolocation and Country State
    isLoadingGPS,
    userCountryCode,
    isLocationModalOpen,
    setLocationModalOpen,
    openLocationModal: () => setLocationModalOpen(true),
    closeLocationModal: () => setLocationModalOpen(false),

    // Saved Addresses & Auth State
    isAuthenticated,
    customer,
    savedAddresses,
    isLoadingAddress,
    isDeletingAddress: deleteMutation.isPending,
    isSettingDefault: setDefaultMutation.isPending,

    // Core Actions
    useCurrentLocation,
    selectAddress,
    selectSavedAddress,
    deleteSavedAddress,
    setDefaultAddress,
  }
}
